import { readProductEditor } from './product-editor.js';
import { PRODUCTS as DEFAULT_PRODUCTS, ORDER, PAGE, ROUTINE, FAQ } from './pdp-data.js';
import { mountThemeFeature } from './editor-lifecycle.js';
import { money, priceHTML, tryActive, tryCents } from './money.js';

mountThemeFeature(lifecycle => {
const { IntersectionObserver, ResizeObserver, setTimeout, setInterval, requestAnimationFrame } = lifecycle;
/* ============================================================
   SKIN BEAUTIFUL MD — PDP engine, Oura direction
   Oura's mechanisms, rebuilt in vanilla (no Framer, no library):
     1  pinned media stage: position:sticky; top = nav + 24 (CSS)
     2  carousel — translateX track, 560ms ease-out, autoplay 5s,
        pager dot→36×6 pill with an inner progress bar, arrows, live region
     3  sticky product bar + header hide/show on scroll DIRECTION
        (La Mer mechanism, Oura clothes) — replaces the floating info bar
     4  current-product purchase
     4b quick-switch bridge cards under the hero (SURI mechanism)
     5  whileInView reveals: opacity 0→1 .75s, .1s stagger; headings blurFadeIn
     6  single-open accordion, height 0↔auto ≈.35s, plus→minus
     7  add to bag (front-end; Shopify-ready) · 8 drawer (shared)
   ============================================================ */

// Shopify port: js-built image urls piggyback on the environment's own asset base
// (the tokens.css link the page already loaded), so they ride the dev proxy
// locally and the CDN in production — never a theme-version-pinned remote host.
const ASSET_BASE = (document.querySelector('link[href*="tokens.css"]')?.href || '').split('tokens.css')[0];
const IMG = ASSET_BASE;

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const html = document.documentElement;
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const cents = dollars => Math.round(Number(dollars) * 100);   /* #sbmd-config prices are dollars */
const productId = html.dataset.product || 'ageless';
const editor = readProductEditor(DEFAULT_PRODUCTS, FAQ, ROUTINE, productId);
const PRODUCTS = editor.products;
const P = PRODUCTS[productId];
const P_CENTS = cents(P.price);
/* ---------- bound text ---------- */
/* Price lines render in this same synchronous pass, TRY state included
   (money.js reads the cookie/flag at run time, no request), so the TRY
   variant lands with the price itself rather than after it. */
$$('[data-bind]').forEach(el => {
  const k = el.dataset.bind;
  if (k === 'price') el.innerHTML = priceHTML(P_CENTS);
  else if (k === 'slabImg' && P.slides[0].image) { el.src = P.slides[0].image; el.removeAttribute('srcset'); el.alt = P.slides[0].alt || ''; }
  else if (k === 'slabImg') { el.src = `${IMG}${P.slides[0].src}-960.webp`; el.srcset = `${IMG}${P.slides[0].src}-640.webp 640w, ${IMG}${P.slides[0].src}-960.webp 960w`; el.sizes = '(min-width: 750px) 40vw, 100vw'; el.alt = P.slides[0].alt; }
  else if (k in P) el.textContent = P[k];
});

/* ---------- 2. carousel ---------- */
const track = $('[data-track]'), pager = $('[data-pager]'), live = $('[data-live]');
if (track && pager) {
track.innerHTML = P.slides.map((s, i) => `<figure class="slide" role="group" aria-roledescription="slide" aria-label="${i + 1} of ${P.slides.length}" ${s.attributes || ''}><img src="${s.image || `${IMG}${s.src}-960.webp`}" ${s.image ? '' : `srcset="${IMG}${s.src}-640.webp 640w, ${IMG}${s.src}-960.webp 960w, ${IMG}${s.src}-1440.webp 1440w"`} sizes="(min-width: 1024px) 62vw, 100vw" alt="${s.alt}" ${i ? 'loading="lazy"' : 'fetchpriority="high"'} decoding="async"></figure>`).join('');
pager.innerHTML = P.slides.map((_, i) => `<button class="dot${i ? '' : ' is-active'}" role="tab" aria-selected="${i === 0}" aria-label="Slide ${i + 1}" data-dot="${i}"><span class="dot-bar" aria-hidden="true"></span></button>`).join('');
const dots = $$('.dot', pager);
let cur = 0, timer = 0, progressStart = 0;
const n = P.slides.length;
function go(i, user = false) {
  cur = (i + n) % n;
  track.style.transform = `translateX(${-cur * 100}%)`;
  dots.forEach((d, j) => { d.classList.toggle('is-active', j === cur); d.setAttribute('aria-selected', j === cur); });
  live.textContent = `Slide ${cur + 1} of ${n}`;
  restartAutoplay();
}
function restartAutoplay() {
  clearInterval(timer);
  dots.forEach(d => { d.classList.remove('is-running'); void d.offsetWidth; });
  if (reduced || n < 2) return;
  dots[cur].classList.add('is-running');       /* CSS animates the inner bar over --o-autoplay */
  timer = setInterval(() => go(cur + 1), parseFloat(getComputedStyle(html).getPropertyValue('--o-autoplay')) * 1000);
}
lifecycle.listen($('[data-prev]'), 'click', () => go(cur - 1, true));
lifecycle.listen($('[data-next]'), 'click', () => go(cur + 1, true));
lifecycle.listen(pager, 'click', e => { const d = e.target.closest('[data-dot]'); if (d) go(+d.dataset.dot, true); });
const car = $('[data-carousel]');
lifecycle.listen(car, 'mouseenter', () => { clearInterval(timer); dots[cur].classList.add('is-paused'); });
lifecycle.listen(car, 'mouseleave', () => { dots[cur].classList.remove('is-paused'); restartAutoplay(); });
/* touch swipe */
let x0 = null;
lifecycle.listen(track, 'touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
lifecycle.listen(track, 'touchend', e => { if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; x0 = null; if (Math.abs(dx) > 40) go(cur + (dx < 0 ? 1 : -1), true); }, { passive: true });
go(0);
}

const others = ORDER.filter(id => id !== P.id);
$$('[data-total]').forEach(el => { el.innerHTML = priceHTML(P_CENTS); });
if ($('[data-other-links]')) $('[data-other-links]').innerHTML = others.map(id => `<a href="${PAGE[id]}">${PRODUCTS[id].short}</a>`).join(' · ');

/* ---------- 4b. quick switch — SURI bridge cards, Oura clothes ---------- */
const BRIDGE_IMG = { ageless: 'bd-ageless', citrus: 'bd-citrus', deep: 'bd-deep' };
if ($('[data-bridge]')) $('[data-bridge]').innerHTML = ORDER.map(id => { const p = PRODUCTS[id]; const on = id === P.id; return `
  <a class="bridge-card${on ? ' is-current' : ''}" href="${PAGE[id]}"${on ? ' aria-current="page"' : ''}>
    <img src="${IMG}${BRIDGE_IMG[id]}-p720.webp" srcset="${IMG}${BRIDGE_IMG[id]}-p720.webp 720w, ${IMG}${BRIDGE_IMG[id]}-p1080.webp 1080w" sizes="(min-width: 750px) 30vw, 100vw" alt="" loading="lazy" decoding="async">
    <span class="bridge-card-top"><span class="bridge-card-name">${p.short}</span><span class="bridge-card-hook">${p.hook}</span></span>
    <span class="bridge-card-bottom"><span class="chip chip--light">${on ? 'Viewing' : money(cents(p.price))}</span><span class="btn-circle" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none"><path d="M2 8h11M8.5 3.5 13 8l-4.5 4.5" stroke="currentColor" stroke-width="1.4"/></svg></span></span>
  </a>`; }).join('');

/* ---------- 3. sticky product bar + header hide — La Mer scroll-direction mechanism ---------- */
const prodbar = $('[data-prodbar]'), panelCta = $('.cta-card .cta');
let lastY = scrollY;
function setBar(on) { if (!prodbar) return; if (prodbar.classList.contains('is-on') !== on) { prodbar.classList.toggle('is-on', on); prodbar.setAttribute('aria-hidden', !on); } }
function barCheck() {
  if (!prodbar || !panelCta) return;
  const y = scrollY, down = y > lastY;
  const past = panelCta.getBoundingClientRect().bottom < 65;   /* buy area above La Mer's 65px line */
  if (!past || y <= 1) { setBar(false); document.body.classList.remove('hdr-hidden'); }
  else if (down) { setBar(true); document.body.classList.add('hdr-hidden'); }
  else { setBar(false); document.body.classList.remove('hdr-hidden'); }
  lastY = y;
}

/* ---------- 7. add to bag ---------- */
const toast = $('.toast'); let toastTimer = 0;
function showToast(qty) {
  if (!toast) return;
  const lineCents = P_CENTS * qty;
  $('[data-toast-name]', toast).textContent = P.short;
  $('[data-toast-price]', toast).textContent = money(tryActive() ? tryCents(lineCents) : lineCents);
  toast.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2400);
}
$$('[data-atc]').forEach(b => lifecycle.listen(b, 'click', async () => {
  const nativeForm = document.querySelector('[data-native-product-form]');
  if (nativeForm) { nativeForm.requestSubmit(); return; }
  const items = [P];
  const qtyEl = $('[data-qty]');
  const qty = Math.min(99, Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1));
  b.disabled = true;
  try {
    await window.SBMDShopifyCart.add(items.map(p => p.id), qty);
    showToast(qty);
  } catch (error) {
    console.error('Unable to add items to the Shopify cart.', error);
  } finally {
    b.disabled = false;
  }
}));

/* ---------- feature cards, routine grid, accordion, specs ---------- */
if ($('[data-cards]')) $('[data-cards]').innerHTML = P.cards.map((c, i) => `
  <article class="fcard reveal" style="--i:${i}" ${c.attributes || ''}>
    <img src="${c.image || `${IMG}${c.img}-480.webp`}" ${c.image ? '' : `srcset="${IMG}${c.img}-480.webp 480w, ${IMG}${c.img}-864.webp 864w"`} sizes="(min-width: 750px) 30vw, 100vw" alt="" loading="lazy" decoding="async">
    <p class="body-lg"><strong>${c.lead}</strong> ${c.copy}</p>
  </article>`).join('');
const ICON = { sun: 'M12 7a5 5 0 100 10 5 5 0 000-10zM12 2v2M12 20v2M2 12h2M20 12h2', moon: 'M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z', clock: 'M12 3a9 9 0 100 18 9 9 0 000-18zM12 7v5l3 2', face: 'M12 3a9 9 0 100 18 9 9 0 000-18zM9 10h.01M15 10h.01M9 15c1 1 5 1 6 0', body: 'M12 3a3 3 0 100 6 3 3 0 000-6zM6 21v-5a6 6 0 0112 0v5', shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z' };
if ($('[data-routine-grid]')) $('[data-routine-grid]').innerHTML = editor.routine.map(([ic, t, s, attributes], i) => `<div class="icon-row reveal" style="--i:${i}" ${attributes || ''}><span class="icon-ring" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="${ICON[ic]}" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span><span class="icon-t">${t}</span><span class="icon-s">${s}</span></span></div>`).join('');
if ($('[data-accordion]')) $('[data-accordion]').innerHTML = editor.faq.map(([q, a, open, attributes], i) => `
  <div class="acc-item${open ? ' is-open' : ''}" ${attributes || ''}>
    <button class="acc-head" type="button" aria-expanded="${!!open}" aria-controls="acc-${i}" id="acch-${i}"><span>${q}</span><span class="acc-ic" aria-hidden="true"></span></button>
    <div class="acc-wrap" id="acc-${i}" role="region" aria-labelledby="acch-${i}" ${open ? '' : 'hidden'}><div class="acc-body"><p>${a}</p>${i === 0 ? '<a class="btn btn--sm btn--outline" href="/#routine">Learn more</a>' : ''}</div></div>
  </div>`).join('');
/* the science, deeper — one row per active, sources inline, anchors into /science/ */
const sciAcc = $('[data-sci-accordion]');
if (sciAcc && P.science) {
  sciAcc.innerHTML = P.science.map((s, i) => `
  <div class="acc-item" ${s.attributes || ''}>
    <button class="acc-head" type="button" aria-expanded="false" aria-controls="sci-${i}" id="scih-${i}"><span>${s.name}<span class="acc-sub">${s.role}</span></span><span class="acc-ic" aria-hidden="true"></span></button>
    <div class="acc-wrap" id="sci-${i}" role="region" aria-labelledby="scih-${i}" hidden><div class="acc-body"><p>${s.copy}</p>${s.src ? `<p class="acc-sources">${s.src}</p>` : ''}</div></div>
  </div>`).join('');
  const sciLink = $('[data-sci-link]');
  if (sciLink && !sciLink.dataset.editorLink) sciLink.href = `/pages/science#${P.id}`;
}
if ($('[data-included]')) $('[data-included]').innerHTML = [P.name, P.size, P.form, ...P.keyIngredients.slice(0, 2)].map(x => `<li>${x}</li>`).join('');
const SPEC = [
  ['Key ingredients', 'M4 12c3-6 7-6 10 0s7 6 10 0', P.keyIngredients],
  ['Texture & feel', 'M12 3s6 7 6 11a6 6 0 01-12 0c0-4 6-11 6-11z', P.texture],
  ['When to use', 'M12 3a9 9 0 100 18 9 9 0 000-18zM12 7v5l3 2', P.when],
  ['Where', 'M12 3a9 9 0 100 18 9 9 0 000-18zM9 10h.01M15 10h.01M9 15c1 1 5 1 6 0', P.where],
  ['Free from', 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z', P.freeFrom || ['<span class="slot">To be confirmed</span>']],
  ['Origin & standards', 'M4 20C8 6 16 4 20 4c0 8-4 14-12 16-2 .5-4 0-4 0z', P.origin || ['<span class="slot">To be confirmed</span>']],
];
if ($('[data-specs]')) $('[data-specs]').innerHTML = SPEC.map(([t, d, items]) => `<div class="spec"><span class="spec-ic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="${d}" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></span><h3>${t}</h3><ul>${items.map(x => `<li>${x}</li>`).join('')}</ul></div>`).join('');

/* ---------- 6. single-open accordions (FAQ + the science, deeper) ---------- */
$$('[data-accordion], [data-sci-accordion]').forEach(acc => lifecycle.listen(acc, 'click', e => {
  const head = e.target.closest('.acc-head'); if (!head) return;
  const item = head.closest('.acc-item'), wrap = item.querySelector('.acc-wrap'), opening = !item.classList.contains('is-open');
  $$('.acc-item.is-open', acc).forEach(o => { if (o !== item) collapse(o); });
  opening ? expand(item, wrap) : collapse(item);
  function expand(it, w) { it.classList.add('is-open'); it.querySelector('.acc-head').setAttribute('aria-expanded', 'true'); w.hidden = false; if (reduced) return; const h = w.scrollHeight; w.style.height = '0px'; requestAnimationFrame(() => { w.style.height = h + 'px'; }); lifecycle.listen(w, 'transitionend', () => { w.style.height = ''; }, { once: true }); }
  function collapse(it) { const w = it.querySelector('.acc-wrap'); it.classList.remove('is-open'); it.querySelector('.acc-head').setAttribute('aria-expanded', 'false'); if (reduced) { w.hidden = true; return; } w.style.height = w.scrollHeight + 'px'; requestAnimationFrame(() => { w.style.height = '0px'; }); lifecycle.listen(w, 'transitionend', () => { w.hidden = true; w.style.height = ''; }, { once: true }); }
}));

/* ---------- 5. reveals (whileInView once) ---------- */
const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { threshold: 0.33 });
$$('.reveal, .blur-in').forEach(el => io.observe(el));

/* ---------- nav frost ---------- */
const header = $('#header'); let frame = 0;
lifecycle.listen(window, 'scroll', () => { if (frame) return; frame = requestAnimationFrame(() => { frame = 0; header?.classList.toggle('is-scrolled', scrollY > 8); barCheck(); }); }, { passive: true });
header?.classList.toggle('is-scrolled', scrollY > 8); barCheck();

/* ---------- 8. drawer (shared Blend component) ---------- */
const drawer = $('.drawer'), scrim = $('.drawer-scrim');
if (drawer && scrim) {
  const openers = $$('[data-drawer]'), closeBtn = $('.drawer-close', drawer), list = $('[role="tablist"]', drawer);
  const tabs = $$('[role="tab"]', list), panels = tabs.map(t => document.getElementById(t.getAttribute('aria-controls')));
  const select = tab => { tabs.forEach(t => { const on = t === tab; t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; }); panels.forEach(p => p.toggleAttribute('hidden', p.id !== tab.getAttribute('aria-controls'))); };
  tabs.forEach((t, i) => { lifecycle.listen(t, 'click', () => select(t)); lifecycle.listen(t, 'keydown', e => { let j = null; if (e.key === 'ArrowRight') j = (i + 1) % tabs.length; if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length; if (j !== null) { e.preventDefault(); tabs[j].focus(); select(tabs[j]); } }); });
  let lastFocus = null;
  const open = id => { lastFocus = document.activeElement; const t = tabs.find(t => t.dataset.tab === id); if (t) select(t); html.classList.add('drawer-open'); openers.forEach(b => b.setAttribute('aria-expanded', 'true')); drawer.removeAttribute('aria-hidden'); setTimeout(() => (list.querySelector('[aria-selected="true"]') || closeBtn).focus(), reduced ? 0 : 200); };
  const close = () => { html.classList.remove('drawer-open'); openers.forEach(b => b.setAttribute('aria-expanded', 'false')); drawer.setAttribute('aria-hidden', 'true'); lastFocus?.focus(); };
  openers.forEach(b => lifecycle.listen(b, 'click', e => { e.preventDefault(); open(b.dataset.drawer); }));
  lifecycle.listen(closeBtn, 'click', close); lifecycle.listen(scrim, 'click', close);
  lifecycle.listen(drawer, 'click', e => { if (e.target.closest('a[href^="#"]')) close(); });   // in-page links close the drawer (Blend engine parity)
  lifecycle.listen(window, 'keydown', e => { if (e.key === 'Escape' && html.classList.contains('drawer-open')) close(); });
  drawer.setAttribute('aria-hidden', 'true');
}
/* info "i" → simple native dialog text */
/* the eyebrow's info note — a quiet card in the page's own grammar,
   not a browser dialog (C9, Sam) */
const infoBtn = $('[data-info]');
if (infoBtn) {
  const eyebrow = infoBtn.closest('.eyebrow');
  eyebrow.insertAdjacentHTML('beforeend',
    `<div class="eyebrow-note" id="eyebrow-note" role="note">
       <p class="eyebrow-note-lead"></p>
       <p class="eyebrow-note-copy"></p>
     </div>`);
  const note = $('.eyebrow-note', eyebrow);
  $('.eyebrow-note-lead', note).textContent = `${P.eyebrow}.`;
  $('.eyebrow-note-copy', note).textContent = P.use;
  infoBtn.setAttribute('aria-expanded', 'false');
  infoBtn.setAttribute('aria-controls', 'eyebrow-note');
  const setNote = on => { note.classList.toggle('is-on', on); infoBtn.setAttribute('aria-expanded', String(on)); };
  lifecycle.listen(infoBtn, 'click', () => setNote(!note.classList.contains('is-on')));
  lifecycle.listen(document, 'click', e => { if (note.classList.contains('is-on') && !eyebrow.contains(e.target)) setNote(false); });
  lifecycle.listen(document, 'keydown', e => { if (e.key === 'Escape' && note.classList.contains('is-on')) { setNote(false); infoBtn.focus(); } });
}
/* email signup */
$$('form[data-signup]').forEach(form => lifecycle.listen(form, 'submit', e => { e.preventDefault(); const note = $('.signup-note', form); const v = $('input[type="email"]', form).value.trim(); if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { note.textContent = 'Please check that email and try again.'; note.classList.add('err'); } else { note.textContent = 'You’re in — sent sparingly.'; note.classList.remove('err'); form.reset(); } }));

});
